import {capitalize} from 'es-toolkit';
import {action, makeObservable} from 'mobx';

import {pollAsync} from '@yourcoach/async-utils';

import type {CoreOrB2bEnv} from '../../utils/coreOrB2b/types';
import type {Combine, Nullable} from '../../utils/utility-types';
import type {ConferenceApiModel} from '../conference';
import type {OfferApiModel} from '../offer';
import type {ApiMethodName} from '../types';
import type {
  BNUserAuthExternalAuthParams,
  BNUserAuthLoginParams,
  BNUserAuthSendCodeParams,
  BNUserAuthSignupParams,
} from '../types/bnrks/methods';
import type {
  CorePublicShortLinksReadParams,
  CoreUserAccountsSendCodeParams,
  CoreUserAuthCheckCodeParams,
  CoreUserAuthCheckUserParams,
  CoreUserAuthExternalAuthParams,
  CoreUserAuthLoginParams,
  CoreUserAuthLogoutParams,
  CoreUserAuthRefreshParams,
  CoreUserAuthSendCodeParams,
  CoreUserAuthSetPushTokenParams,
  CoreUserAuthSignupParams,
  CoreUserProfileDeleteParams,
} from '../types/core/methods';
import type {CoreSubtenantApiModel} from '../types/core/models';
import type {Role, UserApiModel} from '../user';
import type {
  Access,
  ExternalAuthAction,
  ExternalAuthUserInfo,
  IntroCall,
  SendCodeResult,
  Session,
  StoredSession,
} from './types';

import {appStateManager, type AppStateManager} from '../../utils/appState';
import {not} from '../../utils/conditionalUtils';
import {
  datetimeObjToISOString,
  ISOStringToDatetimeObj,
} from '../../utils/datetime';
import {envManager} from '../../utils/env';
import {ErrorResourceUserExpiredSession} from '../../utils/errors/api';
import {HttpRequestError} from '../../utils/errors/app';
import {logger} from '../../utils/logger';
import {platform} from '../../utils/platform';
import {storage} from '../../utils/storage';
import {storageTokenManager} from '../../utils/storageToken';
import {AsyncBusyManager} from '../../utils/store';
import {isTestEnv} from '../../utils/test-utils/isTestEnv';
import {accountStore} from '../account';
import {currentUserStore} from '../user';
import {apiRequest} from '../utils/apiRequest';

export const ACCESS_JWT_TTL = 60 * 1000;

// 10 seconds before the token expires
export const RENEW_ACCESS_DELAY_DELTA = 10 * 1000;

type BusyReason =
  | 'checkCode'
  | 'checkUser'
  | 'deleteAccount'
  | 'externalAuth'
  | 'login'
  | 'logout'
  | 'sendCode'
  | 'signup';

// we need it if we can't access to storage
let sessionInMemory: Nullable<Session> = null;

type AccessTokenStoredData = {
  expires: number;
  token: string;
};

export class AuthStore {
  static CHECK_USER_CAPTCHA_ACTION = 'check_user';
  static LOGIN_CAPTCHA_ACTION = 'login';
  static SEND_CODE_CAPTCHA_ACTION = 'send_code';
  static SIGNUP_CAPTCHA_ACTION = 'signup';
  busy = new AsyncBusyManager<BusyReason>();
  error: null | {code: number | string; message: string};
  private _isAppActive = true;
  private _renewAccessTimeout: Nullable<number> = null;
  private _renewRequest: Nullable<ReturnType<typeof apiRequest>> = null;

  constructor() {
    this.busy.observe(this, {
      checkCode: 'checkCode',
      checkUser: 'checkUser',
      deleteAccount: 'deleteAccount',
      externalAuth: 'externalAuth',
      login: 'login',
      logout: 'logout',
      sendCode: 'sendCode',
      signup: 'signup',
    });

    makeObservable(this, {
      _deleteSession: !isTestEnv ? action.bound : false,
      _ensureAccessTokenIsNotRefreshing: !isTestEnv ? action.bound : false,
      _onReceiveAccessToken: !isTestEnv ? action.bound : false,
      _renewAccessToken: !isTestEnv ? action.bound : false,
      _restoreSession: !isTestEnv ? action.bound : false,
      _saveSession: !isTestEnv ? action.bound : false,
      checkCode: !isTestEnv ? action.bound : false,
      login: !isTestEnv ? action.bound : false,
      logout: !isTestEnv ? action.bound : false,
      sendCode: !isTestEnv ? action.bound : false,
      shortLinkLogin: !isTestEnv ? action.bound : false,
      signup: !isTestEnv ? action.bound : false,
    });

    /* istanbul ignore if */
    if (!isTestEnv) {
      setInterval(() => {
        // I don't know why, but this stopped freezing timer for renewing access token
      }, 1000);
    }

    appStateManager.onStateChange(this.onAppStateChange.bind(this));

    Object.defineProperty(apiRequest, '__deleteSession', {
      configurable: false,
      enumerable: false,
      value: this._deleteSession,
      writable: false,
    });

    Object.defineProperty(currentUserStore, '__renewAccessToken', {
      configurable: false,
      enumerable: false,
      value: this._renewAccessToken,
      writable: false,
    });

    Object.defineProperty(
      currentUserStore,
      '__ensureAccessTokenIsNotRefreshing',
      {
        configurable: false,
        enumerable: false,
        value: this._ensureAccessTokenIsNotRefreshing,
        writable: false,
      },
    );

    Object.defineProperty(accountStore, '__renewAccessToken', {
      configurable: false,
      enumerable: false,
      value: this._renewAccessToken,
      writable: false,
    });

    Object.defineProperty(accountStore, '__saveSession', {
      configurable: false,
      enumerable: false,
      value: this._saveSession,
      writable: false,
    });

    this._listenForStorageChange();
  }

  __getStorageKeyForEnv(env: CoreOrB2bEnv) {
    const prefix = this.getStorageKeyPrefixForEnv(env);

    return {
      accessTokenData: `${prefix}acc_d`,
      session: `${prefix}session`,
      storageToken: `${prefix}st_tkn`,
    } as const;
  }

  _deleteSession() {
    const storageKeys = this.getStorageKeys();

    Object.values(storageKeys).forEach(key => {
      storage.removeItem(key);
    });

    sessionInMemory = null;

    apiRequest.setAccessToken({
      expires: 0,
      token: null,
    });

    this._clearRenewAccessTimeout();

    currentUserStore.clear();
  }

  async _ensureAccessTokenIsNotRefreshing() {
    await pollAsync(() => !this._renewRequest, {
      timeout: 30 * 1000,
    });
  }

  async _onReceiveAccessToken({
    requestDuration = 0,
    token,
  }: {
    requestDuration?: number;
    token: string;
  }) {
    const {expires: accessExpiresTimestamp} =
      this.getAccessTokenTtl(requestDuration);
    const storageKeys = this.getStorageKeys();

    const accessTokenData: AccessTokenStoredData = {
      expires: accessExpiresTimestamp,
      token,
    };

    await storage.setItem(
      storageKeys.accessTokenData,
      JSON.stringify(accessTokenData),
    );

    apiRequest.setAccessToken({
      expires: accessExpiresTimestamp,
      token,
    });

    this._scheduleAccessTokenRenew(accessExpiresTimestamp);
  }

  async _renewAccessToken() {
    // Don't refresh token if app is not active
    if (!this._isAppActive) {
      this._debug('Skip token refresh - app is not active');

      return;
    }

    const storedSession = (await this._readStoredSession()) || sessionInMemory;
    const storageKeys = this.getStorageKeys();

    if (!storedSession) {
      logger.error(new Error('No session to renew access token'));

      return;
    }

    if (this._renewRequest) {
      this._debug('Refresh already in progress locally, skipping');

      return;
    }

    this._debug('Refreshing access token');

    try {
      const beforeRequestTime = Date.now();

      this._renewRequest = apiRequest<CoreUserAuthRefreshParams>({
        method: 'user.auth.refresh',
        options: {
          cancelId: 'renew_access_token',
          withAuth: false,
        },
        params: {
          session_token: storedSession.token,
        },
      });

      const result: {
        access: Access;
        session: Session;
        storage?: {token: string};
      } = await this._renewRequest;

      const requestDuration = Date.now() - beforeRequestTime;

      const {access, session} = result;

      await this._saveSession(session);

      // Save storage token if present
      if (result.storage?.token) {
        await storage.setItem(storageKeys.storageToken, result.storage.token);
      }

      storageTokenManager.setToken(result.storage?.token || '');

      const {expires: accessExpiresTimestamp} =
        this.getAccessTokenTtl(requestDuration);

      const accessTokenData: AccessTokenStoredData = {
        expires: accessExpiresTimestamp,
        token: access.token,
      };

      await storage.setItem(
        storageKeys.accessTokenData,
        JSON.stringify(accessTokenData),
      );

      apiRequest.setAccessToken({
        expires: accessExpiresTimestamp,
        token: access.token,
      });

      this._scheduleAccessTokenRenew(accessExpiresTimestamp);

      this._renewRequest = null;
    } catch (error) {
      this._renewRequest = null;

      if (apiRequest.isCanceledError(error)) {
        return;
      }

      if (
        error instanceof ErrorResourceUserExpiredSession ||
        HttpRequestError.isBanByIp(error) // Ban by IP
      ) {
        this._deleteSession();

        return;
      }

      this._scheduleAccessTokenRenew(1000);
    }
  }

  async _restoreSession() {
    const session = await this._readStoredSession();

    if (session) {
      const sessionExpiresTimestamp = Date.parse(
        datetimeObjToISOString(session.expires),
      );
      const isSessionAlive = Date.now() < sessionExpiresTimestamp;

      if (isSessionAlive) {
        await this._restoreTokens();

        return true;
      }

      logger.error(new Error('Session is expired after restore'));

      this._deleteSession();

      return false;
    }

    return false;
  }

  async _saveSession(session: Session) {
    const sessionToStore: StoredSession = {
      expires: Date.parse(datetimeObjToISOString(session.expires)),
      id: session._id,
      token: session.token,
    };

    sessionInMemory = session;

    try {
      const storageKeys = this.getStorageKeys();

      await storage.setItem(
        storageKeys.session,
        JSON.stringify(sessionToStore),
      );
    } catch (error) {
      /* istanbul ignore next */
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('Error saving session', error);
      }

      logger.error(error);
    }
  }

  async checkCode({
    code,
    email,
    phone,
  }: {
    code: string;
    email?: string;
    phone?: string;
  }) {
    try {
      const params: CoreUserAuthCheckCodeParams = {
        code,
        email,
        phone,
      };

      await apiRequest<CoreUserAuthCheckCodeParams>({
        method: 'user.auth.check_code',
        options: {
          withAuth: false,
        },
        params,
      });

      this.error = null;
    } catch (error) {
      this.error = error;

      throw error;
    }
  }

  async checkUser(
    {
      email,
      phone,
      platform = 'web',
    }: {
      email?: string;
      phone?: string;
      platform?: CoreUserAuthCheckUserParams['platform'];
    },
    captcha: string,
  ) {
    try {
      let result: SendCodeResult;

      if (envManager.isB2b) {
        result = await apiRequest<BNUserAuthSendCodeParams>({
          method: 'user.auth.send_code',
          options: {
            withAuth: false,
          },
          params: {
            dry_run: true,
            email,
            phone,
            platform,
          },
        });
      } else {
        result = await apiRequest<CoreUserAuthCheckUserParams>({
          method: 'user.auth.check_user',
          options: {
            captcha,
            withAuth: false,
          },
          params: {
            email,
            phone,
            platform,
          },
        });
      }

      this.error = null;

      return result;
    } catch (error) {
      this.error = error;

      throw error;
    }
  }

  async deleteAccount() {
    try {
      logger.event('account_delete');

      await apiRequest<CoreUserProfileDeleteParams>({
        method: 'user.profile.delete',
      });

      logger.event('account_delete_success');

      this._deleteSession();

      this.error = null;
    } catch (error) {
      this.error = error;

      throw error;
    }
  }

  async externalAuth({
    agent,
    appId,
    authAction = 'login',
    autoMatch,
    clientId,
    clientSecret,
    introCall,
    token,
    userInfo,
  }: {
    agent: string;
    appId?: string;
    authAction: ExternalAuthAction;
    autoMatch?: boolean;
    clientId: string;
    clientSecret: string;
    introCall?: IntroCall;
    token: string;
    userInfo?: ExternalAuthUserInfo;
  }) {
    type Params = Combine<
      BNUserAuthExternalAuthParams,
      CoreUserAuthExternalAuthParams
    >;

    const params: Params = {
      action: authAction,
      app_id: appId,
      client_id: clientId,
      client_secret: clientSecret,
      device: agent,
      push_token: null,
      token,
    };

    if (userInfo) {
      const processGender = (gender?: Nullable<string>) => {
        const allowedValues = ['female', 'male', 'na', 'unknown'] as const;

        if (gender && allowedValues.includes(gender)) {
          return gender as (typeof allowedValues)[number];
        }

        return null;
      };

      params.user_info = {
        demographics: {
          date_of_birth: userInfo.demographics.dateOfBirth,
          email: userInfo.demographics.email,
          first_name: userInfo.demographics.firstName,
          gender: processGender(userInfo.demographics.gender),
          last_name: userInfo.demographics.lastName,
          phone: userInfo.demographics.phone,
          state: userInfo.demographics.state,
          timezone: envManager.isCore
            ? userInfo.demographics.timezone
            : undefined,
          zipcode: userInfo.demographics.zipcode,
        },
        partner_id: userInfo.partnerId ? String(userInfo.partnerId) : null,
      };

      if (envManager.isB2b) {
        // @ts-expect-error: b2b still has this param
        params.user_info.additional_data = userInfo.additionalData;
        params.user_info.coach_reason = userInfo.coachReason;
      } else if (userInfo.additionalData) {
        type CoreUserInfo = NonNullable<
          Required<CoreUserAuthExternalAuthParams['user_info']>
        >;

        const additionalData = userInfo.additionalData;

        const onboarding: CoreUserInfo['onboarding'] = {
          answers: additionalData.map(item => ({
            answer: Array.isArray(item.answer)
              ? item.answer
              : // Dario send us plain string as an answer
                [item.answer],
            question: capitalize(item.question),
          })),
          finished: null,
          type: 'external',
        };

        (params.user_info as CoreUserInfo).onboarding = onboarding;
      }

      if (userInfo.populationIds?.length) {
        params.population_ids = userInfo.populationIds;
      }

      if (userInfo.lead_payload_data) {
        params.lead_payload_data = userInfo.lead_payload_data;
      }

      if (userInfo.externalOfferId) {
        params.external_offer_id = userInfo.externalOfferId;
      }
    }

    if (envManager.isCore) {
      params.auto_match = autoMatch;

      params.intro_call =
        introCall === undefined
          ? undefined
          : {
              start_date: introCall.startDate ?? null,
              type: introCall.type,
            };
    }

    try {
      const beforeRequestTime = Date.now();

      const {
        access,
        conference,
        offer,
        session,
        storage,
        user,
        widget_flow: widgetFlow,
      }: {
        access?: Access;
        conference?: ConferenceApiModel;
        offer?: OfferApiModel;
        session: Nullable<Session>;
        storage?: {token: string};
        user: UserApiModel;
        widget_flow?: CoreSubtenantApiModel['features']['widget_flow'];
      } = await apiRequest<Params>({
        method: 'user.auth.external_auth',
        options: {withAuth: false},
        params,
      });

      const requestDuration = Date.now() - beforeRequestTime;

      if (session) {
        await this._saveSession(session);
      }

      storageTokenManager.setToken(storage?.token || '');

      if (access) {
        this._onReceiveAccessToken({requestDuration, token: access.token});
      }

      currentUserStore.setUser({
        ...user,
        avatar: null,
        coach_logo: null,
        coach_subtenants: [],
        coach_video: null,
        subtenant: null,
      });
      currentUserStore.fetch();

      this.error = null;

      return {
        confirmationType: widgetFlow?.required_confirmation ?? null,
        introCall: conference
          ? {
              id: conference._id,
              shortUrl: conference.short_url,
            }
          : undefined,
        usedOfferId: offer?._id,
      };
    } catch (error) {
      this.error = error;

      throw error;
    }
  }

  getStorageKeys() {
    const storageKeys = this.__getStorageKeyForEnv(
      envManager.isB2b ? 'b2b' : 'core',
    );

    return storageKeys;
  }

  async login(
    {
      agent,
      anyRole,
      code,
      email,
      externalId,
      phone,
      platform = 'web',
      pushToken = null,
      roles,
    }: {
      agent: string;
      anyRole?: boolean;
      code: string;
      email?: string;
      externalId?: string;
      phone?: string;
      platform?: CoreUserAuthLoginParams['platform'];
      pushToken?: null | string;
      roles?: Role[];
    },
    captcha?: string,
  ) {
    type LoginParams = BNUserAuthLoginParams | CoreUserAuthLoginParams;

    const params: LoginParams = {
      code,
      device: agent,
      email,
      external_id: externalId,
      phone,
      platform,
      push_token: pushToken,
    };

    try {
      const beforeRequestTime = Date.now();

      const {
        access,
        session,
        storage,
        user,
      }: {
        access: Access;
        session: Session;
        storage?: {token: string};
        user: UserApiModel;
      } = await apiRequest<LoginParams>({
        method: 'user.auth.login',
        options: {
          captcha,
          withAuth: false,
        },
        params,
      });

      const requestDuration = Date.now() - beforeRequestTime;

      if (
        roles &&
        !roles[anyRole ? 'some' : 'every'](role => user.roles.includes(role))
      ) {
        throw {
          code: 'login.not_supported_roles',
          message:
            "You are trying to log in as a user that doesn't have permissions",
        };
      } else {
        await this._saveSession(session);

        storageTokenManager.setToken(storage?.token || '');

        this._onReceiveAccessToken({requestDuration, token: access.token});

        currentUserStore.setUser({
          ...user,
          avatar: null,
          coach_logo: null,
          coach_subtenants: [],
          coach_video: null,
          subtenant: null,
        });
        currentUserStore.fetch();

        this.error = null;
      }
    } catch (error) {
      this.error = error;

      throw error;
    }
  }

  async logout() {
    try {
      await apiRequest<CoreUserAuthLogoutParams>({
        method: 'user.auth.logout',
      });

      this._deleteSession();

      this.error = null;
    } catch (error) {
      this.error = error;

      throw error;
    }
  }

  async sendCode(
    {
      email,
      forAccount,
      linkTo,
      phone,
      platform = 'web',
    }: {
      email?: string;
      forAccount?: boolean;
      linkTo?: CoreUserAuthSendCodeParams['link_to'];
      phone?: string;
      platform?: CoreUserAuthSendCodeParams['platform'];
    },
    captcha?: string,
  ) {
    try {
      type Params = CoreUserAccountsSendCodeParams | CoreUserAuthSendCodeParams;

      const params: Params = {
        email,
        phone,
      };

      if (not(forAccount)) {
        (params as CoreUserAuthSendCodeParams).platform = platform;
      }

      if (linkTo !== undefined) {
        if (envManager.isCore) {
          params.link_to = linkTo;
        } else if (envManager.isB2b && linkTo === 'web') {
          // @ts-expect-error: b2b still has this param
          params.to_website = true;
        }
      }

      let method: ApiMethodName = 'user.auth.send_code';

      if (!envManager.isB2b && forAccount) {
        method = 'user.accounts.send_code';
      }

      const result: SendCodeResult = await apiRequest<Params>({
        method,
        options: {
          captcha,
          withAuth: method === 'user.accounts.send_code',
        },
        params,
      });

      this.error = null;

      return result;
    } catch (error) {
      this.error = error;

      throw error;
    }
  }

  sessionIsActive() {
    return this._restoreSession();
  }

  async setPushToken(token: string, apns?: string) {
    await apiRequest<CoreUserAuthSetPushTokenParams>({
      method: 'user.auth.set_push_token',
      params: {
        apns,
        push_token: token,
      },
    });
  }

  async shortLinkLogin({
    agent,
    platform = 'web',
    shortLinkSlug,
  }: {
    agent: string;
    platform?: CorePublicShortLinksReadParams['platform'];
    shortLinkSlug: CorePublicShortLinksReadParams['url'];
  }) {
    try {
      const beforeRequestTime = Date.now();

      const {
        access,
        session,
        storage,
        user,
      }: {
        access?: Access;
        session?: Session;
        storage?: {token: string};
        user?: UserApiModel;
      } = await apiRequest<CorePublicShortLinksReadParams>({
        method: 'public.short_links.read',
        options: {withAuth: false},
        params: {
          device: agent,
          dry_run: false,
          platform,
          url: shortLinkSlug,
        },
      });

      if (!user) {
        // if we passed slug of not login link
        throw new Error('Authentication failed!');
      }

      const requestDuration = Date.now() - beforeRequestTime;

      if (session) {
        await this._saveSession(session);
      }

      if (storage) {
        storageTokenManager.setToken(storage.token);
      }

      if (access) {
        this._onReceiveAccessToken({requestDuration, token: access.token});
      }

      currentUserStore.setUser({
        ...user,
        avatar: null,
        coach_logo: null,
        coach_subtenants: [],
        coach_video: null,
        subtenant: null,
      });
      currentUserStore.fetch();

      this.error = null;
    } catch (error) {
      this.error = error;

      throw error;
    }
  }

  async signup(
    {
      agent,
      code,
      email,
      externalId,
      isTestClient,
      name,
      partnerId,
      phone,
      platform = 'web',
      pushToken = null,
    }: {
      agent: string;
      code: string;
      email?: string;
      externalId?: string;
      isTestClient?: boolean;
      name: string;
      partnerId?: Nullable<string>;
      phone?: string;
      platform?: CoreUserAuthSignupParams['platform'];
      pushToken?: Nullable<string>;
    },
    captcha?: string,
  ) {
    type SignupParams = BNUserAuthSignupParams | CoreUserAuthSignupParams;

    const params: SignupParams = {
      code,
      device: agent,
      email,
      is_test_client: isTestClient,
      name,
      phone,
      platform,
      push_token: pushToken,
    };

    if (envManager.isCore) {
      if (partnerId) {
        (params as CoreUserAuthSignupParams).partner_id = partnerId;
      }

      if (externalId) {
        (params as CoreUserAuthSignupParams).external_id = externalId;
      }

      if (isTestClient) {
        (params as CoreUserAuthSignupParams).is_test_client = isTestClient;
      }
    }

    try {
      const beforeRequestTime = Date.now();

      const {
        access,
        invites_count,
        session,
        user,
      }: {
        access: Access;
        invites_count: number;
        session: Session;
        user: UserApiModel;
      } = await apiRequest<SignupParams>({
        method: 'user.auth.signup',
        options: {
          captcha,
          withAuth: false,
        },
        params,
      });

      const requestDuration = Date.now() - beforeRequestTime;

      await this._saveSession(session);
      this._onReceiveAccessToken({requestDuration, token: access.token});

      currentUserStore.setUser({
        ...user,
        avatar: null,
        coach_logo: null,
        coach_subtenants: [],
        coach_video: null,
        subtenant: null,
      });
      currentUserStore.fetch();

      this.error = null;

      return {invites_count};
    } catch (error) {
      this.error = error;

      throw error;
    }
  }

  private _cancelRenewRequest() {
    if (this._renewRequest) {
      this._debug('Cancelling renew request');

      this._renewRequest.cancel();

      this._renewRequest = null;
    }
  }

  private async _clearRenewAccessTimeout() {
    if (typeof this._renewAccessTimeout === 'number') {
      this._debug('Clearing renew access timeout');
      clearTimeout(this._renewAccessTimeout);
      this._renewAccessTimeout = null;
    }
  }

  private _debug(message: string, ...args: unknown[]) {
    const shouldLog = false;

    if (shouldLog) {
      const now = new Date();
      const time = now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        hour12: false,
        minute: '2-digit',
        second: '2-digit',
      });

      if (platform.isWeb) {
        // eslint-disable-next-line no-console
        console.log(
          '%c[auth]%c | %c' + time + '%c | %c' + message,
          'color: #4f46e5; font-weight: bold',
          'color: #666666',
          'color: #059669; font-weight: bold',
          'color: #666666',
          'color: #666666',
          ...args,
        );
      } else {
        // eslint-disable-next-line no-console
        console.log(
          '\x1b[34m[auth]\x1b[90m | ' +
            '\x1b[32m' +
            time +
            '\x1b[90m | ' +
            message,
          ...args,
        );
      }
    }
  }

  private async _listenForStorageChange() {
    if (platform.isWeb && typeof window !== 'undefined') {
      await pollAsync(() => envManager.isReady);

      const storageKeys = this.getStorageKeys();

      /**
       * We need to sync session deletion between tabs
       * Also, we don't need to unsubscribe since the store is singleton
       * and it lives as long as the app lives
       */
      storage.onChange(storageKeys.session, (newValue, oldValue) => {
        // session has been deleted by another tab
        if (oldValue && !newValue) {
          logger.error(new Error('Session has been deleted by another tab'));
          this._deleteSession();
        }
      });

      // Listen for access token changes
      storage.onChange(
        storageKeys.accessTokenData,
        async (newValue, oldValue) => {
          if (newValue && newValue !== oldValue) {
            this._debug('Access token updated by another tab');

            try {
              const accessTokenData = JSON.parse(
                newValue,
              ) as AccessTokenStoredData;

              apiRequest.setAccessToken({
                expires: accessTokenData.expires,
                token: accessTokenData.token,
              });
            } catch (error) {
              this._debug('Error parsing access token data:', error);
            }
          }
        },
      );

      // Listen for storage token changes
      storage.onChange(storageKeys.storageToken, (newValue, oldValue) => {
        if (newValue && newValue !== oldValue) {
          this._debug('Storage token updated by another tab');
          storageTokenManager.setToken(newValue);
        }
      });
    }
  }

  private async _readStoredSession(): Promise<Nullable<Session>> {
    try {
      const storageKeys = this.getStorageKeys();
      const sessionStr = await storage.getItem(storageKeys.session);

      if (!sessionStr) {
        return null;
      }

      const session = JSON.parse(sessionStr) as StoredSession;

      if (session) {
        const storedSession = {
          _id: session.id,
          expires: ISOStringToDatetimeObj(
            new Date(session.expires).toISOString(),
          ),
          token: session.token,
        };

        return storedSession;
      }

      return null;
    } catch (error) {
      /* istanbul ignore next */
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('Error reading session', error);
      }

      logger.error(error);

      this._deleteSession();

      return null;
    }
  }

  private async _restoreTokens() {
    try {
      const storageKeys = this.getStorageKeys();
      const [accessTokenDataStr, storageToken] = await Promise.all([
        storage.getItem(storageKeys.accessTokenData),
        storage.getItem(storageKeys.storageToken),
      ]);

      const now = Date.now();

      let accessTokenData: AccessTokenStoredData | null = null;

      if (accessTokenDataStr) {
        try {
          accessTokenData = JSON.parse(
            accessTokenDataStr,
          ) as AccessTokenStoredData;
        } catch (error) {
          this._debug('Error parsing access token data:', error);
        }
      }

      const isTokenExpired = accessTokenData
        ? now >= accessTokenData.expires
        : true;

      if (accessTokenData) {
        this._debug('Restoring access token from storage');

        apiRequest.setAccessToken({
          expires: accessTokenData.expires,
          token: accessTokenData.token,
        });
      }

      if (storageToken) {
        this._debug('Restoring storage token from storage');
        storageTokenManager.setToken(storageToken);
      }

      if (isTokenExpired) {
        this._debug('Access token is expired, renewing immediately');
        this._renewAccessToken();
      } else if (accessTokenData) {
        this._scheduleAccessTokenRenew(accessTokenData.expires);
      } else if (this._renewAccessTimeout === null) {
        this._renewAccessToken();
      }
    } catch (error) {
      this._debug('Error restoring tokens:', error);

      if (this._renewAccessTimeout === null) {
        this._renewAccessToken();
      }
    }
  }

  private _scheduleAccessTokenRenew(expires: number) {
    this._clearRenewAccessTimeout();

    if (this._isAppActive) {
      const nextRenewIn = Math.max(
        expires - Date.now() - RENEW_ACCESS_DELAY_DELTA,
        0,
      );

      this._debug(`Scheduling access token renewal in ${nextRenewIn}ms`);

      this._renewAccessTimeout = +setTimeout(() => {
        this._renewAccessToken();
      }, nextRenewIn);
    } else {
      this._debug('Skip scheduling token renewal - app is not active');
    }
  }

  /**
   * Get the time to live for the access token and the time when it expires
   * based on the current time and the time when the token was issued.
   *
   * @param delta - Duration of a request to the server in milliseconds that returns the access token.
   */
  private getAccessTokenTtl(delta = 0) {
    const now = Date.now();
    const ttl = ACCESS_JWT_TTL - delta;
    const expires = now + ttl;

    return {
      expires,
      ttl,
    };
  }

  private getStorageKeyPrefixForEnv(env: CoreOrB2bEnv) {
    if (env === 'b2b') {
      return 'b2b_';
    }

    return '';
  }

  private async onAppStateChange(state: AppStateManager.State) {
    switch (state) {
      case 'active': {
        this._isAppActive = true;

        const session = (await this._readStoredSession()) || sessionInMemory;
        const storageKeys = this.getStorageKeys();

        this._debug(
          `App state is active. Is there an active session : ${!!session}`,
        );

        if (this._renewRequest) {
          this._debug('Refresh already in progress locally, skipping');
          break;
        }

        if (session) {
          const accessTokenDataStr = await storage.getItem(
            storageKeys.accessTokenData,
          );

          let accessTokenData: AccessTokenStoredData | null = null;

          if (accessTokenDataStr) {
            try {
              accessTokenData = JSON.parse(
                accessTokenDataStr,
              ) as AccessTokenStoredData;
            } catch (error) {
              this._debug('Error parsing access token data:', error);
            }
          }

          const isTokenExpired = accessTokenData
            ? Date.now() >= accessTokenData.expires
            : true;

          if (isTokenExpired) {
            this._debug('Access token is expired, renewing');
            this._renewAccessToken();
          } else if (accessTokenData) {
            this._scheduleAccessTokenRenew(accessTokenData.expires);
          } else {
            this._debug('No access token data found, renewing immediately');
            this._renewAccessToken();
          }
        }
        break;
      }
      case 'background': {
        this._debug('App state is background');
        this._cancelRenewRequest();
        this._clearRenewAccessTimeout();

        this._isAppActive = false;
        break;
      }
      default:
        break;
    }
  }
}

export const authStore = new AuthStore();
